import {INormalizedProduct} from "./NormalizedProduct";
import {IDictionary} from "./ICollectionResponse";
import {IWorkspaceItem} from "./Workspace";
import {IPurchaseRequirement} from "./Order";

export interface IExtension {
    id: string;
    category: string;
    name: string;
    description: string;
    ready: boolean;
    statusMessage: string;
    image?: string;
    user?: string;
    additionalData: IAdditionalData[];
    configUrl: string;
    hasSampleLoad: boolean;
    hasValidation: boolean;
    mapToVendor?: boolean;
    defaultVendor?: string;
}

export interface IExtensionFolioSubextensionReference {
    identifier?: string;
    sourceFolio?: string;
    name?: string;
}

export interface IExtensionFieldInstallOptions {
    field: string;
    options: string[];
}

export enum FrequencyOption {
    Daily = "daily",
    Weekly = "weekly",
    Monthly = "monthly",
    Once = "once",
}
export enum TimeWindow {
    TwelveAMSixAM = "12 AM - 6 AM",
    SixAMTwelvePM = "6 AM - 12 PM",
    TwelvePMSixPM = "12 PM - 6 PM",
    SixPMTwelveAM = "6 PM - 12 AM",
}
export enum DayOfWeek {
    Sunday = "sunday",
    Monday = "monday",
    Tuesday = "tuesday",
    Wednesday = "wednesday",
    Thursday = "thursday",
    Friday = "friday",
    Saturday = "saturday",
}

export interface IImportFrequency {
    frequency: FrequencyOption;
    time: TimeWindow;
    dayOfWeek: DayOfWeek[];
    dayOfMonth: number[];
}

export interface IInstalledExtension {
    /** The connector's logo, carried on the row from the catalog record it was installed from. */
    image?: string;
    id: string;
    name: string;
    source: string;
    enabled: boolean;
    extensionType: string;
    vendor: string;
    additionalData: IAdditionalData[];
    runOrder?: number;
    nickname: string;
    allowAdHocItems?: boolean;
    useLinkedItems?: boolean;
    link?: string;
    linkName?: string;
    linkedItemType?: string;
    allowPrice?: boolean;
    schedule?: string;
    mappings?: IImportMapping[];
    allowFileLibrary?: boolean;
    steps?: IExtensionStep[];
    subOrganization?: string;
    user?: string;
    targetCatalog?: string;
    staticData?: {[key:string]: string};
    displayMode?: string;
    selectionSource?: string;
    hasValidation?: boolean;
    isSubExtension?: boolean;
    createProducts?: boolean;
    /** When the credentials were last checked against the remote system. ISO 8601. */
    lastValidated?: string;
    /** Whether that last check succeeded. Undefined means it has never been validated. */
    lastValidationSuccess?: boolean;
    /** The connector's human-readable explanation of the last validation outcome. */
    lastValidationMessage?: string;
    /**
     * How many required settings this install is still waiting on, counted by the server (MissingSettings)
     * because a protected field arrives here as a placeholder whether or not it has ever been set.
     *
     * Reported ahead of the validation state: an integration nobody has finished filling in has not
     * failed, and a credential check cannot prove anything while a required field is empty.
     */
    missingRequiredSettings?: number;
}

export interface IAdditionalData {
    maxValue?: number;
    topic?: string;
    isFilterSearch?: boolean;
    field: string;
    description: string;
    fieldType: string;
    defaultValue: string;
    protected: boolean;
    dataType: string;
    hasOptions: boolean;
    hasFieldMetadata?: boolean;
    adHocOnlyField?: boolean;
    searchable?: boolean;
    optionsFields?: { type: string, field: string }[]
    optional: boolean;
    fieldFilter?: IAdditionalDataFieldFilter;
    step?: string;
    parentField?: string;
    // Whether the API tokenizes ({{{token}}} → value) this field's value when the extension runs. Mirrors the
    // API's AdditionalField.SupportsTokens (serialized `supportsTokens`). Only these fields are worth offering
    // token autosuggest for — a token in any other field is left as literal text.
    supportsTokens?: boolean;
    mask?: string;
    maskChar?: string;
    maskFormat?: string;
    friendlyNameUrl?: string;
    toolTip?: string;
}

export interface IExtensionStep {
    order: number;
    name: string;
    requiredFields?: string[]
    identifier: string;
    flag?: string;
    showItems?: boolean
}

export interface IAdditionalDataFieldFilter {
    filterType: string;
    target: string;
    value: string;
}

export interface IMultipleRealTimeExtensionResponse {
    success: boolean;
    error: string;
    items: IMultipleRealTimeExtensionResponseItem[];
}

export interface IMultipleRealTimeExtensionResponseItem {
    id: string;
    vendors: IMultipleRealTimeExtensionResponseVendor[];
}

export interface IMultipleRealTimeExtensionResponseVendor {
    vendorName: string;
    vendorPartNumber: string;
    active: boolean;
    onHand?: number;
    manufacturerPartNumber?: string;
    description?: string;
    cost?: number;
    error?: string;
    warehouses?: IRealTimeExtensionResponseItemWarehouse[];
    inStockDate?: Date;
}

export interface IRealTimeExtensionResponse {
    success: boolean;
    error: string;
    items: IRealTimeExtensionResponseItem[];
}

export interface IRealTimeExtensionResponseItem {
    vendorPartNumber: string;
    active: boolean;
    onHand?: number;
    manufacturerPartNumber?: string;
    description?: string;
    cost?: number;
    inStock?: boolean;
    error?: string;
    warehouses?: IRealTimeExtensionResponseItemWarehouse[];
    inStockDate?: Date;
    newMatch?: boolean;
    priceBreaks?: IRealTimeExtensionResponseItemPriceBreak[];
    quantityCost?: number;
    referenceId?: string;
}

export interface IRealTimeExtensionResponseItemPriceBreak {
    quantity: number;
    price: number;
    selected?: boolean;
}

export interface IRealTimeExtensionResponseItemWarehouse {
    warehouse: string;
    onHand: number;
    inStockDate?: Date;
}

export interface IDirectImportExtensionResponse extends IExtensionResponse {
    items?: IWorkspaceItem[]
}

export interface IDirectImportExtensionPreviewResponse extends IExtensionResponse{
    totalItemCount?: number;
    sortableFields?: string[];
    filterableFields?: string[];
    items?: IDirectImportItem[]
}
export interface IDirectImportExtensionPreviewRequest {
    objectId?: string;
    sourceName: string;
    page?: number;
    sortBy?: string;
    filterField?: string;
    filterValue?: string;
    sortByDescending?: boolean;
    pageSize?: number;
}

export interface IDirectImportExtensionRequest {
    items: IDirectImportItem[]
    catalog: string;
    workspaceId: string;
    /** Recreate source bundles as org bundles on the workspace (default true server-side). */
    importBundles?: boolean;
    /** Import/attach the source company + contact to the workspace (default true server-side). */
    attachContact?: boolean;
    /**
     * Create a catalog product for any line nothing matched, instead of a workspace-only ad-hoc line
     * (default false server-side). The one-click quote flow sets it to true up the catalog.
     */
    createMissingProducts?: boolean;
}

export interface IDirectImportItem {
    manufacturerPartNumber?: string;
    cost?: number;
    manufacturer?: string;
    name?: string;
    quantity?: number;
    vendorPartNumber?: string;
    active?: boolean;
    inStock?: boolean;
    onHand?: number;
    description?: string;
    source?: string;
    acProduct?: string;
    opportunity?: string;
    salesOrder?: string;
    price?: number;
    location?: string;
    company?: string;
    orderQuantity?: number;
    productClass?: string;
    purchaseDate?: string
    vendor?: string;
    margin?: number;
    businessUnit?: string;
    status?: string;
    /** The source system's id for this record (e.g. the CW catalog item recid) — deep links + re-import keys. */
    externalId?: string;
    /** Groups bundle members with their header: the source id of the bundle line this item belongs to. */
    bundleInstanceId?: string;
    /** True on the row that represents the bundle itself. */
    isBundleHeader?: boolean;
    bundleName?: string;
    [Key: string]: string | boolean | number | Date | undefined;
}

export interface ICustomOption {
    [Key: string]: string | boolean | number | undefined;
}

export interface IExtensionResponse {
    success: boolean;
    redirectUrl?: string;
    error: string;
    message?: string;
    messageHeader?: string;
    products?: string[];
}

export interface IRecommendationResponse extends IExtensionResponse {
    matchedProducts: IRecommendationMatchedProduct[];
    possibleProducts: IRecommendationMatchedProduct[];
}

export interface IRecommendationMatchedProduct {
    productId?: string;
    score: number;
    scorer: string;
    source: string;
    matchGroup: string;
    matchedTo?: string;
    quantity?: number;
    manufacturerPartNumber?: string;
    name?: string;
    product?: INormalizedProduct;
}

export interface IProductReference {
    catalog: string;
    id: string;
    quantity?: number;
}

export interface IExtensionField {
    fieldType: "text" | "password" | "number";
    fieldName: string;
    options?: string[] | number[];
    required: boolean;
    optionApi?: string;
    description: string;
}

export interface IExtensionConfig {
    version: number;
    fields: IExtensionField[];
    postApi: string;
}

export interface IExtensionInstallRequest {
    additionalData: {[key:string]:string};
    extensionLevel?: string;
    extensionNickname: string;
    vendor?: string;
    catalog?: string;
    mappings?: IImportMapping[];
    schedule?: string;
    existingInstalledId?: string;
    extensionType?: string;
    validationActions?: string[];
    createProducts?: boolean;
    vpnIsMfp?: boolean;
}

export interface IFolioValidationRequest{
    additionalData: {[key:string]:string};
    validationActions?: string[];
    folioId?: string;
}

export interface IExtensionOptionsResponse {
    success: boolean;
    error?: string;
    options: IExtensionOption[];
    /** Total matches across all pages, set by paged suggestions responses for a paginator. */
    totalCount?: number;
}

export interface IExtensionOption {
    value: string
    display: string,
    default?: boolean,
    additionalProperties?: IDictionary<string | number | boolean>
}

// ── Dynamic field-map metadata ──────────────────────────────────────────────
// Returned by an extension's fieldMetadata endpoint: the fields available on the selected form/layout, which a
// dynamicFieldMap control renders as typed inputs. Mirrors the ExtensionFieldMetadata SDK contract.
export interface IExtensionFieldMetadataResponse {
    success: boolean;
    error?: string;
    warnings?: string[];
    fields?: IFieldDescriptor[];
}

export interface IFieldDescriptor {
    key: string;
    label: string;
    // string | text | multiLineText | richText | integer | decimal | money | boolean | date | datetime | time | singleSelect | multiSelect
    dataType: string;
    required: boolean;
    validation?: IExtensionFieldValidation;
    options?: IExtensionOption[];
    isCustom: boolean;
    group?: string;
}

export interface IExtensionFieldValidation {
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    regex?: string;
    allowMultiple?: boolean;
}

export interface IExtensionLink {
    type: string;
    acId: string;
    acCatalog: string;
    externalId: string;
    link: string;
}

export interface IGetExtensionLinksRequest {
    products: {
        acId: string;
        acCatalog: string;
    }[];
}
export interface IGetExtensionLinksResponse {
    links: IExtensionLink[];
}

export interface ISavedExtensionField {
    extensionId: string;
    key: string;
    value: string;
}

export interface ISavedExtensionFieldValue {
    value: string;
}

export interface IGetExtensionFieldsResponse {
    fields: ISavedExtensionField[];
}
export interface IProcurementOrderRequirementsResponse {
    requirementOptions?: IPurchaseRequirement[]
}

export interface IGetExtensionLinksRequest {
    products: {
        acId: string;
        acCatalog: string;
    }[];
}
export interface IGetExtensionLinksResponse {
    links: IExtensionLink[];
}
export interface IFreightOptionResponse {
    error?: string;
    success?: boolean;
    freightOptions?: IFreightOption[]
}
export interface IFreightOption {
    carrierIdentifier?: string;
    carrierName?: string;
    estimatedDays?: number;
    freightAmount?: number;
    shipFromLocation?: string;
}
export interface IProcurementCsvGenerationRequest{
    procurementItemIds?: string[];
}

export interface ICreateCsvRequest {
    reportType?: string;
    entityType?: string;
    gridId?: string;
    lookupConditions?: ILookupConditions;
}

export interface IReportStatusLog {
    status?: string;
    reportType?: string;
    url?: string;
    message?: string;
    timestamp?: string;
    username?: string;
    id?: string;
}

export interface ILookupConditions {
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    searchBy?: string;
    sortBy?: string;
    sortDirection?: string;
    includedFields?: string[];
}

export interface IExtensionPotentialMatchResponse {
    success: boolean;
    error?: string;
    products: IExportPotentialMatch[];
}

export interface IExportPotentialMatch {
    acId: string;
    id: string;
    mfp: string;
    name: string;
}

export interface IImportExtensionMappingSampleRequest {
    installedExtensionId?: string;
    additionalFields: {[key:string]: string};
}

export interface IImportExtensionMappingSampleResponse {
    columns: string[][];
    success: boolean;
    error: string;
}

export interface IImportMapping {
    column: number;
    field: string;
    attribute?: string;
    /** Unit the column is in, for weight/length/width/height only. Imports convert to lb / in; unset means already lb / in. */
    unit?: string;
}

export interface IExtensionLog {
    extensionId: string;
    extensionNickname: string;
    success: boolean;
    message?: string;
    timestamp: Date;
    action?: string;
    timeTake?: number;
    username?: string;
    errorMessage?: string;
    productCount?: number;
    productsCreated?: number;
    productsSkipped?: number;
    productsUpdated?: number;
    /** Catalog Sync only: products linked to a metadata base product for the first time in this run. Absent when the org is not entitled. */
    productsLinkedToMetadata?: number;
    /** Catalog Sync only: already-linked products whose metadata was refreshed because the base document was newer. */
    productsMetadataRefreshed?: number;
    recommendedProductCount?: number;
    potentialMatchProductCount?: number
}

export interface IExtensionFile {
    user?: string;
    name: string;
    notes?: string;
    version?: number;
    enabled: boolean;
    extensionId: string;
    id: string;
}

// A single entry in the centralized extension activity log (folios + standalone extensions).
// Mirrors AdaptiveCatalog.Api.Shared.Models.V_7._0.Extensions.ExtensionActivity.
export interface IExtensionActivity {
    id: string;
    timestamp: Date;
    context?: string;
    contextName?: string;
    isFolio: boolean;
    subExtension?: string;
    subExtensionName?: string;
    activityType?: string;
    user?: string;
    message?: string;
    success?: boolean;
    error?: string;
    /** Sub-organization (customer) this activity was scoped to, when the install is sub-org scoped. */
    subOrganization?: string;
    productsCreated?: number;
    productsUpdated?: number;
    productsSkipped?: number;
    /** Catalog Sync only: products linked to a metadata base product for the first time in this run. Absent when the org is not entitled. */
    productsLinkedToMetadata?: number;
    /** Catalog Sync only: already-linked products whose metadata was refreshed because the base document was newer. */
    productsMetadataRefreshed?: number;
    recordCount?: number;
    durationSeconds?: number;
    taskId?: string;
    /** For settings-update entries: which settings/fields changed (names only, never values). */
    changedFields?: string[];
    references?: IExtensionActivityReference[];
}

export interface IExtensionActivityReference {
    referenceType: string;
    referenceId: string;
    label?: string;
}

export interface IUnmatchedExtensionProduct {
    id: string;
    extensionId: string;
    sourceType: string;
    source: string;
    sourceId: string;
    created: Date;
    name?: string;
    manufacturerPartNumber?: string;
    manufacturer?: string;
    status?: string;
    category?: string;
    subCategory?: string;
    msrp?: number;
    upc?: string;
    cost?: number;
    onHand?: number;
    inStock?: boolean;
    active?: boolean;
    description?: string;
    confirmedMatch?: string;
    confirmedCreateNew?: boolean;
    possibleMatches?: IUnmatchedExtensionProductPossibleMatch[];
}

export interface IUnmatchedExtensionProductPossibleMatch {
    acId: string;
    name?: string;
    manufacturerPartNumber?: string;
    manufacturer?: string;
}

/** One capability's own credential check outcome. */
export interface ISubExtensionValidation {
    lastValidated?: string;
    success?: boolean;
    message?: string;
}

export interface IInstalledExtensionFolio {
    schedule?: string;
    vendor?: string;
    targetCatalog?: string;
    id?: string;
    name?: string;
    nickname?: string;
    sourceExtensionFolio?: IExtensionFolio;
    subExtensionInstalledFields?: { [key: string]: { [key: string]: string } };
    commonInstalledFields?: {[key: string]: string }
    subExtensionHashes?: {[key: string]: string }
    enabled?: boolean;
    enabledSubExtensions?: string[];
    hasValidation?: boolean
    /**
     * Per-capability credential check results, keyed by sub-extension identifier. A folio whose
     * capabilities carry their own credentials has no single answer worth showing beside one of them —
     * the folio-level stamp is a roll-up of all of them.
     */
    subExtensionValidations?: { [identifier: string]: ISubExtensionValidation };
    extensionLevel?: string;
    createProducts?: boolean;
    globalStorefrontMinimumPrice?: boolean;
    syncAllCompanies?: boolean;
    syncAllContacts?: boolean;
    syncAllLocations?: boolean;
    /** How many months back a Purchase History sub-extension reaches on its first run. Defaults to 6. */
    purchaseHistoryMonthsBack?: number;
    /** When the credentials were last checked against the remote system. ISO 8601. */
    lastValidated?: string;
    /** Whether that last check succeeded. Undefined means it has never been validated. */
    lastValidationSuccess?: boolean;
    /** The connector's human-readable explanation of the last validation outcome. */
    lastValidationMessage?: string;
}

export interface IValidateExtensionResponse {
    documentationUrl?: string | undefined;
    success: boolean;
    error?: string;
    message?: string;
    messageHeader?: string;
    validationMessages?: string[]
}

export interface ISubExtension {
    identifier: string;
    category: string;
    name: string;
    description?: string;
    cron?: string;
    displayMode?: string;
    allowFileLibrary: boolean;
    steps?: IExtensionStep[];
    url: string;
    installCallUrl?: string;
    sampleUrl?: string;
    uninstallCallUrl?: string;
    taskStatusUrl?: string;
    additionalData?: IAdditionalData[];
    mapToVendor?: boolean;
    validateName?: string;
    enabled?: boolean;
    extensionType?: string;
    /**
     * Where THIS capability's credentials are checked, when it is not the folio's validator. A
     * distributor checks its catalog feed and its API at different endpoints because they are different
     * accounts.
     */
    validateUrl?: string;
    /**
     * The narrower kind within extensionType, carried to the installed record as its extensionSubType.
     * It is what tells the platform to run a capability through something other than a plain URL post --
     * an `import_configurator` is driven by a scheduled configurator, which is why such a sub-extension
     * has an empty url.
     */
    subCategory?: string;
    /**
     * How to obtain the credentials THIS capability needs, shown beside its own fields. Separate from the
     * folio's links because one distributor issues different credentials per capability -- an FTP account
     * for the catalog file, an API client id and secret for real-time pricing -- from different places.
     */
    helpLinks?: IHelpLink[];
    globalStorefrontMinimumPrice?: boolean;
    /**
     * For an import capability: values its file uses that the importer should read as something else, such as
     * D&H's I / O stock flag. Applied in the import callback over any the extension sends for the same value.
     */
    valueMaps?: IImportValueMap[];
}

/** One value an import file uses for a field, and what the importer reads it as. */
export interface IImportValueMap {
    /** The import mapping field the value appears in: inStock, active, status, ... */
    field: string;
    /** The value as it appears in the file. Matched ignoring case and surrounding whitespace. */
    from: string;
    to: string;
}


/** A single "how do I get these credentials" pointer published by a folio. */
export interface IHelpLink {
    url?: string;
    text?: string;
}

/** A folio's PSA quote-link quick-flow declaration — provider wording lives on the folio, not in code. */
export interface IFolioQuoteLink {
    /** Identifier of the direct-import sub-extension the quick-flow link runs. */
    subExtension?: string;
    /** Query string appended to the generated link, carrying the provider's substitution tag
     * (e.g. "opp={recid}" — CW replaces {recid} with the opportunity record id). */
    parameter?: string;
    /** One-line explanation shown above the generated link. */
    description?: string;
    /** Ordered install steps, shown verbatim. */
    instructions?: string[];
    /** Folio endpoint answering whether the connected member may write the provider's quote-link
     * setup table — the gate for offering automatic installation. */
    permissionUrl?: string;
    /** Folio endpoint that installs the quote link in the provider. */
    createUrl?: string;
}

export interface IExtensionFolio {
    id?: string;
    quoteLink?: IFolioQuoteLink;
    subExtensions?: { [key: string]: ISubExtension };
    sharedFields?: IAdditionalData[];
    encryption?: string;
    /**
     * One host for every endpoint the folio names, applied by the API when the folio is used rather than
     * when it is saved. Empty means nothing changes anywhere, which is every folio that exists today.
     */
    baseUrl?: string;
    displayMode?: string;
    defaultVendor?: string;
    supportsSubOrganization: boolean;
    supportsQuantityPriceBreaks?: boolean;
    validateUrl?: string;
    image?: string;
    user?: string;
    name?: string;
    description?: string;
    /** Documentation links shown next to the credential fields. Mirrors the API's folio help links. */
    helpLinks?: IHelpLink[];
    /** Sub-extension identifiers a one-click setup of this folio enables. */
    defaultSubExtensions?: string[];
    /**
     * Which organizations may install this folio. Empty or absent means every organization (a global
     * folio); exactly one entry means that organization owns it, which is also what decides who may edit
     * the record. Never trusted on save -- the server re-scopes an author's folio to their own org.
     */
    organizations?: string[];
    /** Entitlements an organization needs before this folio appears in its catalog. Global folios only. */
    entitlements?: string[];
    /** Product levels this folio is offered at. Global folios only. */
    productLevel?: string[];
    orderRequirementsUrl?: string;
    itemRequirementsUrl?: string;
    freightOptionsUrl?: string;
    carrierOptionsUrl?: string;
    warehouseOptionsUrl?: string;
    orderUpdateUrl?: string;
}

/**
 * One distributor the organization could connect, as returned by `extensions/folios/distributors`.
 * Flattens what the picker needs -- is it installed, is it actually working, what does connecting get
 * you -- so drawing the choices takes a single call.
 */
export interface IDistributorCandidate {
    id: string;
    name?: string;
    description?: string;
    image?: string;
    helpLinks?: IHelpLink[];
    defaultSubExtensions?: string[];
    hasValidation: boolean;
    installed: boolean;
    /** Checklist vocabulary: inProgress, needsReview or complete. Absent when not installed. */
    status?: 'inProgress' | 'needsReview' | 'complete';
    /** The connector's own explanation of a failed check. */
    message?: string;
    /** The installed folio's id, for opening an existing connection's settings. */
    installedId?: string;
    capabilities?: IDistributorCapability[];
}

/** One capability a distributor folio bundles, with the docs for its own credentials. */
export interface IDistributorCapability {
    identifier: string;
    name?: string;
    description?: string;
    /** import, realtime or procurement. */
    category?: string;
    default: boolean;
    enabled: boolean;
    /** Whether this capability's credentials can be checked at all (its own validator, or the folio's). */
    hasValidation: boolean;
    helpLinks?: IHelpLink[];
}

/**
 * One CRM a user can connect from the Getting Started checklist, as returned by
 * `extensions/folios/crm`. It flattens what the guided setup needs (is it installed, does it already
 * have working credentials, what would we turn on) so the picker needs a single call.
 */
export interface ICrmFolioCandidate {
    id: string;
    name: string;
    description?: string;
    helpLinks?: IHelpLink[];
    /** Sub-extension identifiers quick setup enables. */
    defaultSubExtensions: string[];
    /** Friendly names for `defaultSubExtensions`, in the same order. */
    defaultSubExtensionNames: string[];
    /** False when the connector has no validate endpoint: a validation call then reports success
     * without checking anything, so the UI must say "can't verify" instead of showing a pass. */
    hasValidation: boolean;
    /** Every sub-extension the folio declares, with default and current enablement. */
    capabilities?: ICrmFolioCapability[];
    /** Where the global storefront integration slots point, relative to this folio. */
    storefrontSlots?: ICrmFolioStorefrontSlots;
    installed: boolean;
    hasCredentials: boolean;
    lastValidated?: string;
    lastValidationSuccess?: boolean;
}

export interface ICrmFolioCapability {
    identifier: string;
    name?: string;
    description?: string;
    extensionType?: string;
    /** Whether quick setup would turn this on. */
    default: boolean;
    /** Whether the installed record has it on right now. */
    enabled: boolean;
}

/** One global storefront integration slot described relative to a folio: at most one shape holds. */
export interface ICrmFolioStorefrontSlot {
    /** Set when the slot points at this folio's sub-extension. */
    subExtension?: string;
    disabled: boolean;
    /** The slot points at a different integration; taking it over must be a visible choice. */
    assignedElsewhere: boolean;
}

export interface ICrmFolioStorefrontSlots {
    opportunity?: ICrmFolioStorefrontSlot;
    customerLookup?: ICrmFolioStorefrontSlot;
    locationLookup?: ICrmFolioStorefrontSlot;
}

export interface ICrmFolioCapabilitiesRequest {
    enabledSubExtensions: string[];
}

/** Tri-state per slot: undefined leaves it alone, 'DISABLED' switches it off, '' clears it, and
 * anything else names one of the folio's sub-extensions. */
export interface ICrmFolioStorefrontSlotsRequest {
    opportunity?: string;
    customerLookup?: string;
    locationLookup?: string;
}

export const CRM_SLOT_DISABLED = 'DISABLED';

/** Outcome of creating a test opportunity. The record is real and stays in the customer's CRM. */
export interface ICrmTestOpportunityResponse {
    success: boolean;
    error?: string;
    opportunityId?: string;
    /** Link straight to the record, when the connector supplies one. */
    url?: string;
    message?: string;
}

/**
 * The LLM's proposed initial PSA setup for a wedge-sourced organization. Proposal only: nothing in it
 * has been applied anywhere, and credentials never appear in any field.
 */
export interface IPsaProvisioningProposal {
    folioId?: string;
    /** pending | ready | skipped | failed. The wizard polls while pending. */
    status?: string;
    error?: string;
    modelKey?: string;
    created?: string;
    completed?: string;
    /** The model's own 0..1 estimate that this setup fits the organization. */
    confidence?: number;
    rationale?: string;
    proposedSubExtensions?: string[];
    proposedSharedFieldValues?: { [field: string]: string };
    proposedSubExtensionFieldValues?: { [identifier: string]: { [field: string]: string } };
    proposedOpportunitySlot?: string;
    proposedCustomerLookupSlot?: string;
    proposedLocationLookupSlot?: string;
    /** The post-credential second pass: null until requested, then pending | ready | skipped | failed. */
    fieldInferenceStatus?: string;
    fieldInferenceError?: string;
    fieldInferenceCompleted?: string;
    fieldInferenceRationale?: string;
    /** Inferred option-backed field values, keyed by sub-extension identifier then field name. Each
     * value is one of the live option list fetched from the connector. */
    inferredSubExtensionFieldValues?: { [identifier: string]: { [field: string]: string } };
    /** shown | accepted | edited | dismissed, once a person acted on it. */
    decision?: string;
}

export const PSA_PROPOSAL_STATUS = {
    pending: 'pending',
    ready: 'ready',
    skipped: 'skipped',
    failed: 'failed',
} as const;

export const PSA_PROPOSAL_DECISION = {
    shown: 'shown',
    accepted: 'accepted',
    edited: 'edited',
    dismissed: 'dismissed',
} as const;

export interface IPsaProposalDecisionRequest {
    decision?: string;
    /** Per-field verdicts keyed "shared.{field}", "{subExtension}.{field}" or "slot.{name}",
     * each accepted | edited | rejected. Merged server-side, not replaced. */
    fieldDecisions?: { [field: string]: string };
}

// Merged purchase history for a product (storefront orders + CRM won opportunities), returned by
// extensions/purchasehistory/itemHistory. Rows are sorted date desc and capped at 50 by the API.
export type IPurchaseHistorySourceType = 'storefront' | 'opportunity' | 'both';

export interface IPurchaseHistoryItem {
    date: string | null;
    sourceType: IPurchaseHistorySourceType;
    quantity: number;
    price: number | null;
    revenue: number | null;
    companyName: string | null;
    salesRep: string | null;
    orderStatus: string | null;
    storefrontOrderId: string | null;
    orderNumber: number | null;
    storefrontId: string | null;
    opportunityId: string | null;
    orderName: string | null;
    folioId: string | null;
    recordType: string | null;
}

export const EXTENSION_TYPE_REALTIME = 'realtime';
export const EXTENSION_TYPE_IMPORT = 'import';
export const EXTENSION_TYPE_EXPORT = 'export';
export const EXTENSION_TYPE_WEBHOOK = 'webhook';
export const EXTENSION_TYPE_PROCUREMENT = "procurement";
export const EXTENSION_TYPE_DIRECTIMPORT = 'directImport';
export const EXTENSION_TYPE_CUSTOMERIMPORT = 'customerImport';